import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useInvoice } from '../api/hooks';
import { Cell, Section, Skeleton } from '../components/ui';
import { useT, useLocale } from '../i18n';

export function InvoiceDetail() {
  const { uuid = '' } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const locale = useLocale();
  const inv = useInvoice(uuid);
  const data = (inv.data as Record<string, unknown> | undefined) ?? {};
  const inner = (data.data as Record<string, unknown> | undefined) ?? data;

  return (
    <>
      <header style={{ padding: '12px 8px 0' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '8px 12px',
            color: 'var(--tg-button)',
            fontSize: 15,
            fontWeight: 500,
          }}
        >
          <ArrowLeft size={20} strokeWidth={2.4} />
          {t('common.back')}
        </button>
      </header>

      <div style={{ padding: '8px 20px 4px' }}>
        <h1
          style={{
            margin: 0,
            fontSize: 26,
            fontWeight: 700,
            letterSpacing: '-0.02em',
          }}
        >
          {inv.isLoading ? <Skeleton width="50%" height={26} /> : t('invoice.detail.title')}
        </h1>
      </div>

      <Section title={t('invoice.overview')}>
        <Cell
          title={t('invoice.tradeNo')}
          trailing={
            <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 13 }}>
              {String(inner.trade_no || `Invoice#${inner.id ?? ''}`)}
            </span>
          }
        />
        <Cell title={t('invoice.status')} trailing={statusLabel(String(inner.status ?? ''), t)} />
        <Cell
          title={t('invoice.type')}
          trailing={categoryLabel(inner.category ?? inner.type, t)}
        />
        <Cell
          title={t('invoice.amount')}
          trailing={fmtMoney(inner.actual_amount ?? inner.amount, String(inner.currency ?? ''))}
        />
        <Cell
          title={t('invoice.applied')}
          trailing={fmtMoney(
            inner.used_balance ?? inner.applied_balance,
            String(inner.currency ?? ''),
          )}
        />
        <Cell
          title={t('invoice.discount')}
          trailing={
            inner.discount_amount != null && Number(inner.discount_amount) > 0
              ? '-' + fmtMoney(inner.discount_amount, String(inner.currency ?? ''))
              : inner.discount != null && Number(inner.discount) > 0
                ? '-' + fmtMoney(inner.discount, String(inner.currency ?? ''))
                : t('common.dash')
          }
          last
        />
      </Section>

      <Section title={t('invoice.times')}>
        <Cell
          title={t('invoice.createdAt')}
          trailing={fmtTime(
            inner.date ?? inner.created_at ?? inner.create_time ?? inner.created_time,
            locale,
          )}
        />
        {(() => {
          const paidRaw = inner.paid_at ?? inner.pay_time ?? inner.payment_time;
          const expiresRaw =
            inner.due_at ?? inner.expires_at ?? inner.expire_time ?? inner.expiry;
          const hasPaid = typeof paidRaw === 'string' && paidRaw.length > 0;
          return (
            <>
              <Cell
                title={t('invoice.expiresAt')}
                trailing={fmtTime(expiresRaw, locale)}
                last={!hasPaid}
              />
              {hasPaid && (
                <Cell title={t('invoice.paidAt')} trailing={fmtTime(paidRaw, locale)} last />
              )}
            </>
          );
        })()}
      </Section>
    </>
  );
}

function categoryLabel(raw: unknown, t: ReturnType<typeof useT>): string {
  const code = Array.isArray(raw) ? String(raw[0] ?? '') : String(raw ?? '');
  if (!code) return t('common.dash');
  const key = `invoice.cat.${code.toLowerCase()}` as Parameters<typeof t>[0];
  const out = t(key);
  return out === key ? code : out;
}

function statusLabel(s: string, t: ReturnType<typeof useT>): string {
  if (!s) return t('common.dash');
  const lo = s.toLowerCase();
  const keyMap: Record<string, string> = {
    paid: 'invoice.status.paid',
    unpaid: 'invoice.status.unpaid',
    pending: 'invoice.status.pending',
    'partially-paid': 'invoice.status.partiallyPaid',
    partially_paid: 'invoice.status.partiallyPaid',
    cancelled: 'invoice.status.cancelled',
    canceled: 'invoice.status.cancelled',
    failed: 'invoice.status.failed',
    expired: 'invoice.status.expired',
  };
  const k = keyMap[lo];
  return k ? t(k as Parameters<typeof t>[0]) : s;
}

function fmtTime(v: unknown, locale: string): string {
  if (typeof v !== 'string') return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString(locale === 'zh' ? 'zh-CN' : 'en-US');
}

function fmtMoney(v: unknown, currencyRaw: string): string {
  if (v == null) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  const c = currencyRaw.toUpperCase();
  const sign = c === 'USD' || c === '' ? '$' : c + ' ';
  return `${sign}${n.toFixed(2)}`;
}
